import { COLOR_TYPES, ConfigModel, ConfigStyleTag, IColorTypes, IConfigTagStyle } from '../../models';
import { HEXColor } from '../../models/basic/color';
import { ColorService } from '../color.service';
import { P_SMALL_SCALE_FACTOR } from './config.constants';
import { DEFAULT_CONFIG } from './default.config';

/** Id of the `<style>` element that carries the theme; the server writes it, the client takes it over. */
export const THEME_STYLE_ID = 'gs-theme';

/** The suffixes to use after the value */
const TAG_STYLE_SUFFIXES: Record<keyof IConfigTagStyle, string> = {
  family: '',
  size: 'rem',
  lineHeight: 'em',
  weight: '',
  style: '',
  color: '',
  textTransform: '',
  spacingTop: 'px',
  spacingBottom: 'px'
};

/**
 * Characters that could leave the declaration or the `<style>` element. The values come from the
 * customer config and end up in the server-rendered `<head>`, so they are a trust boundary.
 */
const UNSAFE_CSS_VALUE = /[<>{};\\\n\r]/;

/**
 * Whether a value can be written into the stylesheet without affecting the declarations after it.
 * Besides breaking out of the declaration, an opened comment or an unbalanced quote would run to the
 * end of the block and drop every following variable — `setProperty` only ever lost the one broken
 * value.
 * @param value The name or value of a custom property
 * @returns True if the value is safe to write
 */
function isSafeCssValue(value: string): boolean {
  if (UNSAFE_CSS_VALUE.test(value) || value.includes('/*')) return false;
  return (value.split("'").length - 1) % 2 === 0 && (value.split('"').length - 1) % 2 === 0;
}

/**
 * Renders the project theme from the config as a `:root` stylesheet: the color channels
 * (`--gs-{type}-{step}`), the tag styles (`--tag-{tag}-{breakpoint}-{attr}`) and the small text
 * scale factor. Pure function without DOM access, so the SSR server and the browser render the same
 * CSS.
 * @param config The project config; without one only the scale factor is written.
 * @returns The stylesheet as a string.
 */
export function buildThemeCss(config: ConfigModel | undefined): string {
  const variables: string[] = [];
  /**
   * Adds a custom property, dropping values that could break out of the declaration.
   * @param name The custom property name
   * @param value The CSS value
   */
  const set = (name: string, value: string): void => {
    if (!isSafeCssValue(name) || !isSafeCssValue(value)) return;
    variables.push(`${name}:${value}`);
  };

  set('--p-small-scale-factor', String(P_SMALL_SCALE_FACTOR));

  COLOR_TYPES.forEach((key) => {
    const colorConfig = config?.colors?.[key as keyof IColorTypes];
    if (!colorConfig) return;
    Object.entries(colorConfig).forEach(([step, hex]) => {
      const rgbColor = ColorService.hexToRgb(hex as HEXColor);
      set(`--gs-${key}-${step}`, `${rgbColor.r} ${rgbColor.g} ${rgbColor.b}`);
    });
  });

  Object.entries(config?.tagStyles ?? {}).forEach(([tag, responsiveAttr]) => {
    if (!responsiveAttr) return;
    Object.entries(responsiveAttr).forEach(([breakpoint, tagStyle]) => {
      if (!tagStyle) return;
      Object.entries(tagStyle).forEach(([attrKey, attrValue]) => {
        const name = `--tag-${tag}-${breakpoint}-${attrKey}`;
        if (attrKey === 'spacingTop' || attrKey === 'spacingBottom') {
          // No default in DEFAULT_CONFIG: empty/invalid leaves the var unset so the CSS fallback (0.6em/3px) applies.
          // Empty (null/undefined/'') must NOT coerce to 0 (Number('') === 0) — that would shadow the fallback with 0px.
          // A valid 0 is still written (0 is falsy); NaN/negative are rejected so a defined-but-broken var can't shadow the fallback.
          if (attrValue === null || attrValue === undefined || (attrValue as unknown) === '') return;
          const spacingPx = Number(attrValue);
          if (!Number.isFinite(spacingPx) || spacingPx < 0) return;
          set(name, `${spacingPx}px`);
          return;
        }
        attrValue ??= DEFAULT_CONFIG.tagStyles?.[tag as ConfigStyleTag]?.default[attrKey as keyof IConfigTagStyle];
        if (!attrValue) return;
        if (attrKey === 'color') {
          if (ColorService.isHexColor(attrValue as HEXColor)) {
            const rgbColor = ColorService.hexToRgb(attrValue as HEXColor);
            set(name, `${rgbColor.r} ${rgbColor.g} ${rgbColor.b}`);
          } else {
            set(name, `var(--gs-${attrValue})`);
          }
        } else {
          set(name, attrValue + TAG_STYLE_SUFFIXES[attrKey as keyof IConfigTagStyle]);
        }
      });
    });
  });

  return `:root{${variables.join(';')}}`;
}
